import { Alert, Button, Grid, Space, Table, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import { EXAMS_PAGE_SIZE, useExams, type ExamClassDetails, type ExamDetails } from "../app/exams.ts";
import { ExamDrawer } from "../components/ExamDrawer.tsx";

/**
 * School-wide exams (bd-3qg.10, spec story 53), newest first. The deputy director (academic.manage) plans them here; staff holding
 * grading.readAll see every exam, a teacher only the exams and classes they grade.
 */
export function ExamsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { hasPermission } = useAuth();
  const canPlan = hasPermission("academic.manage");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const exams = useExams(page);

  const columns = [
    { title: t("exams.date"), key: "date", width: 110, render: (_: unknown, e: ExamDetails) => formatDate(e.date) },
    { title: t("exams.name"), key: "name", render: (_: unknown, e: ExamDetails) => <Link to={`/grading/exams/${e.id}`}>{e.name}</Link> },
    { title: t("exams.subject"), dataIndex: "subjectName", key: "subject", responsive: ["md" as const] },
    { title: t("exams.maxScore"), key: "maxScore", width: 120, responsive: ["md" as const], render: (_: unknown, e: ExamDetails) => Number(e.maxScore) },
    {
      title: t("exams.classesTitle"),
      key: "classes",
      render: (_: unknown, e: ExamDetails) => (
        <Space wrap size={4}>
          {e.classes.map((c: ExamClassDetails) => (
            <Tag key={c.classId}>{c.className}</Tag>
          ))}
        </Space>
      ),
    },
    { title: t("exams.markCount"), key: "marks", width: 110, render: (_: unknown, e: ExamDetails) => Number(e.markCount) },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("exams.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{canPlan ? t("exams.subtitle") : t("exams.subtitleGrader")}</Typography.Text>
        </div>
        {canPlan && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t("exams.create")}
          </Button>
        )}
      </div>
      {exams.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void exams.refetch()}>{t("app.retry")}</Button>} />}
      <Table<ExamDetails>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={exams.isPending}
        columns={columns}
        dataSource={exams.data?.items ?? []}
        pagination={{ current: page, pageSize: EXAMS_PAGE_SIZE, total: Number(exams.data?.totalCount ?? 0), onChange: setPage, hideOnSinglePage: true }}
        locale={{ emptyText: t("exams.empty") }}
        scroll={{ x: "max-content" }}
      />
      <ExamDrawer open={creating} onClose={() => setCreating(false)} onSaved={(exam) => void navigate(`/grading/exams/${exam.id}`)} />
    </Space>
  );
}
